/**
 * Quick Add Floating Action Button (FAB)
 * Provides rapid access to common actions from any screen
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Plus, Receipt, Trash2, Package, X, Loader2, Check,
  ChevronUp, DollarSign, AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Expense categories (subset for quick entry)
const QUICK_EXPENSE_CATEGORIES = [
  { value: 'food_supplies', label: 'Food Supplies' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'repairs', label: 'Repairs' },
  { value: 'other', label: 'Other' },
];

// Wastage reasons
const WASTAGE_REASONS = [
  { value: 'expired', label: 'Expired' },
  { value: 'spoiled', label: 'Spoiled' },
  { value: 'prep_waste', label: 'Prep Waste' },
  { value: 'spillage', label: 'Spillage' },
  { value: 'other', label: 'Other' },
];

export default function QuickAddFAB() {
  const { outletId, currentOutlet } = useAuth();
  const navigate = useNavigate();
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null); // 'expense' | 'wastage' | 'stock'
  const [submitting, setSubmitting] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  
  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    category: '',
    description: '',
  });
  
  // Wastage form
  const [wastageForm, setWastageForm] = useState({
    item_id: '',
    quantity: '',
    reason: '',
    notes: '',
  });
  
  // Stock form
  const [stockForm, setStockForm] = useState({
    item_id: '',
    quantity: '',
    notes: '',
  });

  // Load inventory items when needed
  const loadInventoryItems = async () => {
    if (inventoryItems.length > 0 || !outletId) return;
    
    setLoadingItems(true);
    try {
      const response = await api.get(`/api/inventory/stock?outlet_id=${outletId}`);
      setInventoryItems(response.data || []);
    } catch (err) {
      console.error('Failed to load inventory:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  // Open action dialogs
  const openExpenseDialog = () => {
    setActiveDialog('expense');
    setIsOpen(false);
  };

  const openWastageDialog = () => {
    loadInventoryItems();
    setActiveDialog('wastage');
    setIsOpen(false);
  };

  const openStockDialog = () => {
    loadInventoryItems();
    setActiveDialog('stock');
    setIsOpen(false);
  };

  // Submit expense
  const handleSubmitExpense = async () => {
    if (!expenseForm.amount || !expenseForm.category || !expenseForm.description) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/api/finance/expenses', {
        outlet_id: outletId,
        amount: parseFloat(expenseForm.amount),
        currency: 'NOK',
        category: expenseForm.category,
        description: expenseForm.description,
        expense_date: new Date().toISOString().split('T')[0],
        status: 'pending_approval',
      });
      
      toast.success('Expense submitted for approval');
      setActiveDialog(null);
      setExpenseForm({ amount: '', category: '', description: '' });
    } catch (err) {
      toast.error('Failed to submit expense');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit wastage
  const handleSubmitWastage = async () => {
    if (!wastageForm.item_id || !wastageForm.quantity || !wastageForm.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const selectedItem = inventoryItems.find(i => i.raw_material_id === wastageForm.item_id);
    
    setSubmitting(true);
    try {
      await api.post('/api/inventory/movements', {
        outlet_id: outletId,
        raw_material_id: wastageForm.item_id,
        movement_type: 'wastage',
        quantity: parseFloat(wastageForm.quantity),
        unit: selectedItem?.unit || 'units',
        notes: `${wastageForm.reason}${wastageForm.notes ? ': ' + wastageForm.notes : ''}`,
        reference_type: 'wastage',
      });
      
      toast.success('Wastage recorded');
      setActiveDialog(null);
      setWastageForm({ item_id: '', quantity: '', reason: '', notes: '' });
    } catch (err) {
      toast.error('Failed to record wastage');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit stock in
  const handleSubmitStock = async () => {
    if (!stockForm.item_id || !stockForm.quantity) {
      toast.error('Please fill all required fields');
      return;
    }
    
    const selectedItem = inventoryItems.find(i => i.raw_material_id === stockForm.item_id);
    
    setSubmitting(true);
    try {
      await api.post('/api/inventory/movements', {
        outlet_id: outletId,
        raw_material_id: stockForm.item_id,
        movement_type: 'received',
        quantity: parseFloat(stockForm.quantity),
        unit: selectedItem?.unit || 'units',
        notes: stockForm.notes || 'Quick stock addition',
        reference_type: 'manual_adjustment',
      });
      
      toast.success('Stock added successfully');
      setActiveDialog(null);
      setStockForm({ item_id: '', quantity: '', notes: '' });
    } catch (err) {
      toast.error('Failed to add stock');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* FAB Container */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Action Buttons (visible when open) */}
        <div className={cn(
          "flex flex-col gap-2 transition-all duration-200",
          isOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        )}>
          <Button
            onClick={openExpenseDialog}
            className="bg-purple-600 hover:bg-purple-700 shadow-lg gap-2"
            data-testid="quick-add-expense"
          >
            <Receipt className="w-4 h-4" />
            Log Expense
          </Button>
          <Button
            onClick={openWastageDialog}
            className="bg-red-600 hover:bg-red-700 shadow-lg gap-2"
            data-testid="quick-add-wastage"
          >
            <Trash2 className="w-4 h-4" />
            Record Wastage
          </Button>
          <Button
            onClick={openStockDialog}
            className="bg-green-600 hover:bg-green-700 shadow-lg gap-2"
            data-testid="quick-add-stock"
          >
            <Package className="w-4 h-4" />
            Add Stock
          </Button>
        </div>

        {/* Main FAB Button */}
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            "w-14 h-14 rounded-full shadow-xl transition-all duration-200",
            isOpen 
              ? "bg-gray-700 hover:bg-gray-800 rotate-45" 
              : "bg-brand-orange hover:bg-brand-orange/90"
          )}
          data-testid="quick-add-fab"
        >
          {isOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Plus className="w-6 h-6" />
          )}
        </Button>
      </div>

      {/* Expense Dialog */}
      <Dialog open={activeDialog === 'expense'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              Quick Expense
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Amount (NOK) *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  placeholder="0.00"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  className="pl-9"
                  data-testid="quick-expense-amount"
                />
              </div>
            </div>
            <div>
              <Label>Category *</Label>
              <Select 
                value={expenseForm.category} 
                onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
              >
                <SelectTrigger data-testid="quick-expense-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {QUICK_EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description *</Label>
              <Input
                placeholder="What was this for?"
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                data-testid="quick-expense-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleSubmitExpense} 
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="quick-expense-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wastage Dialog */}
      <Dialog open={activeDialog === 'wastage'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" />
              Record Wastage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Item *</Label>
              <Select 
                value={wastageForm.item_id} 
                onValueChange={(v) => setWastageForm({ ...wastageForm, item_id: v })}
              >
                <SelectTrigger data-testid="quick-wastage-item">
                  <SelectValue placeholder={loadingItems ? "Loading..." : "Select item"} />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map(item => (
                    <SelectItem key={item.raw_material_id} value={item.raw_material_id}>
                      {item.item_name} ({item.quantity?.toFixed(1)} {item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity *</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="Enter quantity"
                value={wastageForm.quantity}
                onChange={(e) => setWastageForm({ ...wastageForm, quantity: e.target.value })}
                data-testid="quick-wastage-quantity"
              />
            </div>
            <div>
              <Label>Reason *</Label>
              <Select 
                value={wastageForm.reason} 
                onValueChange={(v) => setWastageForm({ ...wastageForm, reason: v })}
              >
                <SelectTrigger data-testid="quick-wastage-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {WASTAGE_REASONS.map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Additional details..."
                value={wastageForm.notes}
                onChange={(e) => setWastageForm({ ...wastageForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleSubmitWastage} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="quick-wastage-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock In Dialog */}
      <Dialog open={activeDialog === 'stock'} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-green-600" />
              Quick Stock Add
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Item *</Label>
              <Select 
                value={stockForm.item_id} 
                onValueChange={(v) => setStockForm({ ...stockForm, item_id: v })}
              >
                <SelectTrigger data-testid="quick-stock-item">
                  <SelectValue placeholder={loadingItems ? "Loading..." : "Select item"} />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.map(item => (
                    <SelectItem key={item.raw_material_id} value={item.raw_material_id}>
                      {item.item_name} ({item.quantity?.toFixed(1)} {item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity *</Label>
              <Input
                type="number"
                step="0.1"
                placeholder="Enter quantity to add"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                data-testid="quick-stock-quantity"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Supplier, reason, etc."
                value={stockForm.notes}
                onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveDialog(null)}>Cancel</Button>
            <Button 
              onClick={handleSubmitStock} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="quick-stock-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Add Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
